import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
	FirstDataRenderedEvent,
} from "ag-grid-community";
import { Grid, TextField } from "@material-ui/core";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import type { WorkOrderPart } from "../../../../models/WorkOrderPart";

interface Props {
	allParts: WorkOrderPart[];
	workOrderParts: WorkOrderPart[];
	onSelectionChanged: (selectedParts: WorkOrderPart[]) => void;
	isLoading: boolean;
}

export const PartsTable: React.FC<Props> = ({
	allParts,
	workOrderParts,
	onSelectionChanged,
	isLoading,
}) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const sortRows = () => {
		gridApi?.applyColumnState({
			state: [{ colId: "PartName", sort: "asc", sortIndex: 0 }],
		});
	};

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		return allParts.map((part: WorkOrderPart) => ({
			...part,
			isSelected: workOrderParts.some(
				(woPart) => woPart.PartName === part.PartName,
			),
		}));
	}, [allParts, workOrderParts]);

	const columns: ColDef[] = [
		{
			headerName: "",
			flex: 1,
			maxWidth: 40,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			sortIndex: 1,
		},
		{
			field: "PartName",
			headerName: "Part Name",
			minWidth: 120,
			flex: 3,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.PartName?.localeCompare(nodeB.data.PartName);
				}
			},
		},
		{
			field: "Description",
			headerName: "Description",
			flex: 8,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.Description?.localeCompare(nodeB.data.Description);
				}
			},
		},
	];

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
		};
	}, []);

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows();
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			// Select rows based on workOrderParts
			if (workOrderParts && workOrderParts.length > 0) {
				params.api.forEachNode((node) => {
					const isSelected = workOrderParts.some(
						(part) => part.PartName === node.data.PartName,
					);
					if (isSelected) {
						node.setSelected(true);
					}
				});
			}
			sortRows();
		},
		[workOrderParts],
	);

	useEffect(() => {
		if (gridApi) {
			gridApi.setGridOption("quickFilterText", filterInput.trim());
		}
	}, [filterInput, gridApi]);

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else {
			gridApi?.hideOverlay();
			if (allParts.length === 0) gridApi?.showNoRowsOverlay();
		}
	}, [gridApi, isLoading, allParts]);

	// Update selections when workOrderParts changes
	useEffect(() => {
		if (gridApi && workOrderParts) {
			gridApi.deselectAll();
			gridApi.forEachNode((node) => {
				const isSelected = workOrderParts.some(
					(part) => part.PartName === node.data.PartName,
				);
				if (isSelected) {
					node.setSelected(true);
				}
			});
		}
	}, [gridApi, workOrderParts]);

	const onGridReady = (event: GridReadyEvent) => {
		setGridApi(event.api);
	};

	return (
		<>
			<Grid
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid item xs={12}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid>
			</Grid>
			<div
				className="ag-theme-alpine"
				style={{ width: "100%", height: "60vh" }}
			>
				<Grid style={{ width: "100%", height: "60vh" }}>
					<AgGridReact
						getRowId={(params) => params.data.PartName}
						rowHeight={30}
						headerHeight={25}
						animateRows={true}
						onGridReady={onGridReady}
						rowData={rows}
						columnDefs={columns}
						defaultColDef={defaultColDef}
						onFirstDataRendered={onFirstDataRendered}
						loadingOverlayComponent={loadingOverlayComponent}
						rowSelection="multiple"
						pagination={true}
						rowMultiSelectWithClick
						onSelectionChanged={onAgGridSelectionChanged}
						suppressContextMenu={true}
					/>
				</Grid>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
